import { Box, Button, Chip, InputAdornment, List, Paper, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Add, AddLocationAltOutlined, ArrowForward, Circle, Close, Search, SwapVert } from '@mui/icons-material';
import { findIndex } from 'lodash';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Colors } from '../../../assets/theme/Colors';
import RouteCard from './RoutesCard';
import { getColorByIndex } from '../../../helpers/colors';
import LocationSearch from './LocationSearch';
import RouteStopListItem from './RouteStopListItem';
import { MARKER_DONOR, MARKER_RECEIVER } from '../../Map/Components/MarkersSelector';

export const dayOfTheWeekValues = [
  { name: 'Sunday', label: 'S' },
  { name: 'Monday', label: 'M' },
  { name: 'Tuesday', label: 'T' },
  { name: 'Wednesday', label: 'W' },
  { name: 'Thursday', label: 'T' },
  { name: 'Friday', label: 'F' },
  { name: 'Saturday', label: 'S' },
];

const selectedLocationsValidator = (locations) => {
  if (locations.length < 2) {
    return false;
  }

  const receiverSelected = locations.find((loc) => loc.type === MARKER_RECEIVER);
  const donorSelected = locations.find((loc) => loc.type === MARKER_DONOR);
  if (!receiverSelected) {
    return false;
  }

  if (!donorSelected) {
    return false;
  }

  if (locations[0]?.type !== MARKER_DONOR) {
    return false;
  }

  if (locations[locations.length - 1]?.type !== MARKER_RECEIVER) {
    return false;
  }

  return true;
};

const RoutesSidePanel = ({
  handleRouteClick,
  handleNewRouteClick,
  handleCancelNewRoute,
  showDonors,
  showReceivers,
  handleShowDonorsClick,
  handleShowReceiversClick,
  handleLocationSelect,
  handleLocationRemove,
  handleLocationDrag,
  onContinueClick,
  routes = [],
  newRouteStep = false,
  locations = [],
  selectedLocations = [],
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [dayFilter, setDayFilter] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor));
  const activeRoutesCounter = routes.length;
  const numberOfPickupsSelected = selectedLocations.filter((loc) => loc.type === MARKER_DONOR).length;
  const numberOfDropOffsSelected = selectedLocations.filter((loc) => loc.type === MARKER_RECEIVER).length;
  const isValid = selectedLocationsValidator(selectedLocations);

  const filtedRoutes = routes
    .filter((route) => dayFilter.every((day) => (route.days || []).includes(day)))
    .filter((route) => {
      const queryUppercase = searchValue.toUpperCase().trim();
      const routeName = route.name;
      const donorNames = (route.destinations || []).filter((d) => d.donor_name).map((d) => d.donor_name);
      const locationNames = (route.destinations || []).map((d) => d.name);

      if (queryUppercase.length <= 2) {
        return true;
      }
      if (
        (routeName || '').toUpperCase().includes(queryUppercase) ||
        donorNames.find((name) => (name || '').toUpperCase().includes(queryUppercase)) ||
        locationNames.find((name) => (name || '').toUpperCase().includes(queryUppercase))
      ) {
        return true;
      }

      return false;
    });

  const handleDayFilterClick = (day) => {
    setDayFilter((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  if (newRouteStep) {
    return (
      <Box sx={{ minWidth: '350px', mr: 0.5, height: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column' }}>
        <Paper elevation={25} sx={{ p: 2, borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body1" sx={{ fontWeight: '700' }}>
              New Route
            </Typography>
            <Button
              startIcon={<Close />}
              size="small"
              variant="text"
              disableRipple
              onClick={handleCancelNewRoute}
              sx={{
                color: 'GrayText',
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
          </Box>

          <Box sx={{ pt: 1 }}>
            <LocationSearch
              locations={locations}
              showDonors={showDonors}
              showReceivers={showReceivers}
              handleLocationChange={handleLocationSelect}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              p: 0.5,
              mt: 1,
              bgcolor: Colors.lightGray,
              borderRadius: 1,
            }}
          >
            <Button
              startIcon={<Circle sx={{ fontSize: '12px !important', color: Colors.primaryColor }} />}
              variant="text"
              fullWidth
              onClick={handleShowDonorsClick}
              sx={{
                px: 2,
                color: Colors.primaryColor,
                textTransform: 'none',
                bgcolor: showDonors ? 'background.paper' : Colors.lightGray,
                fontWeight: 700,
              }}
            >
              Show Donors
            </Button>
            <Button
              startIcon={<Circle sx={{ fontSize: '12px !important', color: Colors.appBar.main }} />}
              variant="text"
              fullWidth
              onClick={handleShowReceiversClick}
              sx={{
                px: 2,
                color: showReceivers ? Colors.appBar.main : 'GrayText',
                bgcolor: showReceivers ? 'background.paper' : Colors.lightGray,
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              Show Agencies
            </Button>
          </Box>
        </Paper>

        <Box sx={{ mt: 2, px: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 700 }}>
            ROUTE STOPS - {selectedLocations.length}
          </Typography>
          <Box display="flex" alignItems="center">
            <SwapVert sx={{ fontSize: '18px' }} color="disabled" />
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              Drag to reorder
            </Typography>
          </Box>
        </Box>
        {!selectedLocations.length && (
          <Box
            sx={{
              m: 2,
              px: 2,
              py: 3,
              borderRadius: 2,
              border: `1px dashed #9B9B9C`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              maxWidth: '350px',
            }}
          >
            <AddLocationAltOutlined sx={{ fontSize: '28px', color: 'text.disabled' }} />
            <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              Start building your route
            </Typography>
            <Typography variant="subtitl1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Click a <span style={{ color: Colors.primaryColor, fontWeight: 700 }}>donor</span> pin on the map to add
              your first pickup, then switch to{' '}
              <span style={{ color: Colors.appBar.main, fontWeight: 700 }}>Agencies</span> to add drop-offs.
            </Typography>
          </Box>
        )}

        <Box sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'scroll' }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleLocationDrag}>
            <SortableContext items={selectedLocations.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <List>
                {selectedLocations.map((loc, index) => (
                  <RouteStopListItem
                    key={loc.id}
                    isDonor={loc.type === MARKER_DONOR}
                    label={loc.title || loc.name || loc.address}
                    onRemove={() => handleLocationRemove(loc)}
                    counter={index + 1}
                    id={loc.id}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        </Box>

        <Paper elevation={25} sx={{ p: 2, borderRadius: 2, mt: 'auto' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              size="small"
              label={`${numberOfPickupsSelected} pickup${numberOfPickupsSelected === 1 ? '' : 's'}`}
              sx={{
                bgcolor: '#d9e6e7',
                color: Colors.primaryColor,
                fontWeight: 700,
                '& .MuiChip-label': { px: 1.25 },
              }}
            />

            <Chip
              size="small"
              label={`${numberOfDropOffsSelected} drop-off${numberOfDropOffsSelected === 1 ? '' : 's'}`}
              sx={{
                bgcolor: '#fdeae1',
                color: Colors.appBar.main,
                fontWeight: 700,
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              variant="contained"
              fullWidth
              endIcon={<ArrowForward />}
              disabled={!isValid}
              sx={{
                mt: 1,
                textTransform: 'none',
                bgcolor: Colors.appBar.main,
                color: Colors.white,
                fontWeight: 700,
                '&:hover': {
                  bgcolor: Colors.appBar.light,
                },
              }}
              onClick={onContinueClick}
            >
              Continue to Stop Details
            </Button>
            {!isValid && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Add at least one pickup and one drop-off to continue
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={25} sx={{ p: 2, borderRadius: 2, mr: 0.5 }}>
        <Box display="flex" justifyContent="space-between">
          <Box>
            <Typography variant="body1" sx={{ fontWeight: '700' }}>
              All Routes
            </Typography>
            <Typography variant="caption" color="GrayText">
              {activeRoutesCounter} active routes
            </Typography>
          </Box>
          <Button
            startIcon={<Add />}
            size="small"
            variant="contained"
            onClick={handleNewRouteClick}
            sx={{
              bgcolor: Colors.appBar.main,
              color: '#fff',
              fontWeight: 700,
              height: '2.5rem',
              px: 1,
              textTransform: 'none',
              '&:hover': {
                bgcolor: Colors.appBar.light,
              },
            }}
          >
            New Route
          </Button>
        </Box>

        <TextField
          sx={{ pt: 1 }}
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          autoComplete="off"
          placeholder="Search routes"
          variant="outlined"
          name="search"
          size="small"
          fullWidth
        />

        <Box pt={1} display="flex" gap={1.25}>
          {dayOfTheWeekValues.map((day) => {
            const daySelected = dayFilter.includes(day.name);

            return (
              <Box
                key={day.name}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '1.5rem',
                  height: '1.5rem',
                  border: `1px solid ${daySelected ? Colors.secondaryColor : 'rgb(162, 162, 162, 0.6)'}`,
                  borderRadius: '50%',
                  backgroundColor: daySelected ? Colors.secondaryColor : 'inherit',
                }}
                onClick={() => handleDayFilterClick(day.name)}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: daySelected ? Colors.white : 'text.secondary' }}
                >
                  {day.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Box sx={{ mt: 1, maxHeight: 'calc(100vh - 250px)', overflow: 'scroll' }}>
        {filtedRoutes.map((route) => {
          const index = findIndex(routes, (sr) => sr.id === route.id);
          return (
            <RouteCard
              key={route.id}
              title={route.name}
              startTime={route.startTime}
              endTime={route.endTime}
              days={route.days}
              formattedDays={route.formattedDays}
              pickups={route.pickups}
              dropoffs={route.dropoffs}
              distance={route.distance}
              duration={route.duration}
              onClick={() => handleRouteClick(route)}
              color={getColorByIndex(index)}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default RoutesSidePanel;
